// src/components/openDialog/EditPranayamaDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemSecondaryAction,
  InputAdornment,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePranayamma } from 'services/pranayamma/updatePranayamma';

const PHASE_TYPES = [
  { value: 'inhale', label: 'Inhale' },
  { value: 'hold', label: 'Hold In' },
  { value: 'exhale', label: 'Exhale' },
];

const EditPranayamaDialog = ({ open, onClose, pranayama, onSave }) => {
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    name: '',
    description: '',
    loops: '10',
    phases: [],
    steps: '',
    benefits: '',
    difficultyLevel: 'Beginner',
  });

  useEffect(() => {
    if (!pranayama || !open) return;

    let parsedSequence = [];
    let parsedBenefits = [];

    try {
      if (typeof pranayama.breathing_sequence === 'string') {
        parsedSequence = JSON.parse(pranayama.breathing_sequence);
      } else if (Array.isArray(pranayama.breathing_sequence)) {
        parsedSequence = pranayama.breathing_sequence;
      }
    } catch (err) {
      console.warn('Parse error breathing_sequence:', pranayama.id, err);
    }

    try {
      if (typeof pranayama.benefits === 'string') {
        parsedBenefits = JSON.parse(pranayama.benefits);
      } else if (Array.isArray(pranayama.benefits)) {
        parsedBenefits = pranayama.benefits;
      }
    } catch (err) {
      console.warn('Parse error benefits:', pranayama.id, err);
    }

    setForm({
      name: pranayama.name || '',
      description: pranayama.description || '',
      loops: pranayama.loops?.toString() || '10',
      phases: parsedSequence.length > 0
        ? parsedSequence.map((s) => ({
            type: s?.type || 'inhale',
            seconds: String(s?.seconds ?? '0'),
          }))
        : [
            { type: 'inhale', seconds: '4' },
            { type: 'hold', seconds: '4' },
            { type: 'exhale', seconds: '4' },
          ],
      steps: pranayama.instructions || '',
      benefits: parsedBenefits.length > 0 ? parsedBenefits.join('\n') : '',
      difficultyLevel: pranayama.difficulty_level || 'Beginner',
    });
  }, [pranayama, open]);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      loops: '10',
      phases: [
        { type: 'inhale', seconds: '4' },
        { type: 'hold', seconds: '4' },
        { type: 'exhale', seconds: '4' },
      ],
      steps: '',
      benefits: '',
      difficultyLevel: 'Beginner',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const allPhasesHaveType = form.phases.every((p) => p.type?.trim() !== '');

  const addPhase = () => {
    if (form.phases.length >= 12) {
      toast.error('Maximum 12 phases allowed');
      return;
    }
    if (!allPhasesHaveType) {
      toast.error('Please select type for all phases first');
      return;
    }
    setForm((prev) => ({
      ...prev,
      phases: [...prev.phases, { type: '', seconds: '4' }],
    }));
  };

  const updatePhase = (index, field, value) => {
    setForm((prev) => {
      const newPhases = [...prev.phases];

      if (field === 'type') {
        // Prevent consecutive same types
        const prevPhase = index > 0 ? newPhases[index - 1] : null;
        if (prevPhase && prevPhase.type === value) {
          toast.error('Consecutive phases cannot be the same type');
          return prev;
        }
      }

      newPhases[index] = { ...newPhases[index], [field]: value };
      return { ...prev, phases: newPhases };
    });
  };

  const removePhase = (index) => {
    if (form.phases.length <= 2) {   // ← changed to 2 to match your schema min 2
      toast.error('At least 2 phases required');
      return;
    }
    setForm((prev) => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index),
    }));
  };

  const movePhase = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === form.phases.length - 1)
    )
      return;

    const newPhases = [...form.phases];
    const target = direction === 'up' ? index - 1 : index + 1;
    [newPhases[index], newPhases[target]] = [newPhases[target], newPhases[index]];
    setForm((prev) => ({ ...prev, phases: newPhases }));
  };

  const updateMutation = useMutation({
    mutationFn: updatePranayamma,
    onSuccess: () => {
      queryClient.invalidateQueries(['pranayammas']);
      resetForm();
      onClose();
      onSave?.();
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to update pranayama');
    },
  });

  const handleSubmit = () => {
    if (!form.name?.trim()) {
      toast.error('Pranayama name is required');
      return;
    }

    if (!allPhasesHaveType) {
      toast.error('All phases must have a selected type');
      return;
    }

    const totalSeconds = form.phases.reduce((sum, p) => sum + Number(p.seconds || 0), 0);
    if (totalSeconds === 0) {
      toast.error('At least one phase must have duration > 0');
      return;
    }

    if (Number(form.loops) < 1) {
      toast.error('Number of rounds must be at least 1');
      return;
    }

    // Enforce no consecutive same types (extra safety)
    for (let i = 1; i < form.phases.length; i++) {
      if (form.phases[i].type === form.phases[i - 1].type) {
        toast.error('Consecutive phases cannot have the same type');
        return;
      }
    }

    const payload = {
      id: pranayama?.id,
      name: form.name.trim(),
      duration: totalSeconds,
      loops: Number(form.loops),
      difficultyLevel: form.difficultyLevel,
      breathingSequence: form.phases.map((p) => ({
        type: p.type,
        seconds: Number(p.seconds) || 0,
      })),
      instructions: form.steps.trim(),
      benefits: form.benefits
        ? form.benefits.split('\n').map((line) => line.trim()).filter(Boolean)
        : [],
      description: form.description.trim(),
    };

    updateMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Edit Pranayama
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} mt={1}>
          {/* Name */}
          <TextField
            autoFocus
            label="Pranayama Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="e.g. Anulom Vilom, Box Breathing"
            fullWidth
            required
          />

          {/* Rounds + Difficulty */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Number of Rounds"
              name="loops"
              value={form.loops}
              onChange={handleChange}
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 1 }}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Difficulty Level</InputLabel>
              <Select
                name="difficultyLevel"
                value={form.difficultyLevel}
                label="Difficulty Level"
                onChange={handleChange}
              >
                <MenuItem value="Beginner">Beginner</MenuItem>
                <MenuItem value="Intermediate">Intermediate</MenuItem>
                <MenuItem value="Advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Breathing Sequence */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Breathing Sequence
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (max 12 phases)
                </Typography>
              </Typography>

              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={addPhase}
                variant="outlined"
                disabled={form.phases.length >= 12 || !allPhasesHaveType}
              >
                Add Phase
              </Button>
            </Box>

            <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
              <List disablePadding>
                {form.phases.map((phase, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ py: 1.5 }}>
                      {/* Type */}
                      <FormControl sx={{ minWidth: 140, mr: 2 }}>
                        <Select
                          value={phase.type}
                          onChange={(e) => updatePhase(index, 'type', e.target.value)}
                          size="small"
                          displayEmpty
                          renderValue={(selected) =>
                            !selected ? (
                              <em style={{ color: '#757575' }}>Select type</em>
                            ) : (
                              PHASE_TYPES.find((opt) => opt.value === selected)?.label || selected
                            )
                          }
                        >
                          {PHASE_TYPES.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Seconds */}
                      <TextField
                        size="small"
                        type="number"
                        label="Seconds"
                        value={phase.seconds}
                        onChange={(e) => updatePhase(index, 'seconds', e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 110, mr: 2 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">s</InputAdornment>,
                        }}
                      />

                      {/* Controls */}
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          onClick={() => movePhase(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => movePhase(index, 'down')}
                          disabled={index === form.phases.length - 1}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removePhase(index)}
                          disabled={form.phases.length <= 2}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>

            {/* Total duration preview */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'right' }}>
              Total per round:{' '}
              <strong>
                {form.phases.reduce((sum, p) => sum + Number(p.seconds || 0), 0)} seconds
              </strong>
            </Typography>
          </Box>

          {/* Instructions */}
          <TextField
            label="Steps / Instructions"
            name="steps"
            value={form.steps}
            onChange={handleChange}
            multiline
            rows={4}
            fullWidth
            placeholder="1. Sit comfortably with spine straight\n2. Close eyes and relax..."
          />

          {/* Benefits */}
          <TextField
            label="Benefits (one per line)"
            name="benefits"
            value={form.benefits}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
            placeholder="• Reduces stress\n• Improves lung capacity\n• Calms the mind"
          />

          {/* Description */}
          <TextField
            label="Short Description (optional)"
            name="description"
            value={form.description}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          size="large"
          disabled={updateMutation.isLoading || !allPhasesHaveType}
        >
          {updateMutation.isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            'Update Pranayama'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPranayamaDialog;